/**
 * Export utilities for Operations Management tables.
 * Uses backend Excel generation via PhpSpreadsheet.
 */

import apiURL from "libs/apiUrl";

export interface ExportParams {
	/** Visible column keys to include in export */
	columns: string[];
	/** API filter parameters */
	filters?: Array<{
		type: string;
		field: string;
		value: unknown;
	}>;
	/** Sort configuration */
	order?: {
		field: string;
		direction: "ASC" | "DESC";
	};
}

/**
 * Build export URL with query parameters.
 * Triggers download by opening the URL.
 *
 * @param path The export endpoint path (e.g., "/admin/operations-management/summary/by-country/export")
 * @param params Export parameters (columns, filters, order)
 */
export function triggerExportDownload(path: string, params: ExportParams): void {
	// Use the same API base URL as the API client
	const url = new URL(path, apiURL);

	// Add columns
	params.columns.forEach((col) => {
		url.searchParams.append("columns[]", col);
	});

	// Add filters
	if (params.filters && params.filters.length > 0) {
		params.filters.forEach((filter, index) => {
			url.searchParams.append(`filters[${index}][type]`, filter.type);
			url.searchParams.append(`filters[${index}][field]`, filter.field);

			// Handle object values (number range, date range)
			if (typeof filter.value === "object" && filter.value !== null) {
				const objValue = filter.value as Record<string, unknown>;
				Object.entries(objValue).forEach(([key, val]) => {
					if (val !== undefined && val !== null && val !== "") {
						url.searchParams.append(`filters[${index}][value][${key}]`, String(val));
					}
				});
			} else {
				url.searchParams.append(`filters[${index}][value]`, String(filter.value));
			}
		});
	}

	// Add order
	if (params.order) {
		url.searchParams.append("order[field]", params.order.field);
		url.searchParams.append("order[direction]", params.order.direction);
	}

	// Trigger download by navigating to the URL
	// This works because the backend returns Content-Disposition: attachment
	window.location.href = url.toString();
}

/**
 * Generate a filename with timestamp (for reference, backend generates actual filename).
 */
export function generateExportFilename(baseName: string): string {
	const now = new Date();
	const timestamp = now.toISOString().split("T")[0]; // YYYY-MM-DD format
	return `${baseName}_${timestamp}`;
}
